import { api } from "_shared/_stores/api";
import AssistantAPICommand from "ai-assistant/_api/commands/assistant-api-command";
import AssistantResponse from "ai-assistant/assistant-response";
import AssistantResponseSchema, { AssistantResponseJSON } from "ai-assistant/assistant-response-schema";
import AssistantConversation from "ai-assistant/conversations/assistant-conversation";
import AssistantConversationSchema, { AssistantConversationJSON } from "ai-assistant/conversations/assistant-conversation-schema";
import AssistantConversationSummary, { AssistantConversationSummarySchema, AssistantConversationSummaryJSON } from "ai-assistant/conversations/assistant-conversation-summary";
import { z } from "zod";

export interface SendAssistantMessageRequest {
  message: string;
  conversationId?: string;
  currentRoute?: string;
}

export interface StagedActionRequest {
  stagedActionId: string;
}

export interface AssistantConversationRequest {
  conversationId: string;
}

/** What a bulk erasure left behind, and why. */
export interface AssistantHistoryErasure {
  deleted: number;
  retainedUnderLegalHold: number;
}

export const aiAssistantApi = api.injectEndpoints({
  endpoints: (build) => ({
    sendAssistantMessage: build.mutation<AssistantResponse, SendAssistantMessageRequest>({
      query: (request) => ({
        url: "ai-assistant",
        method: "POST",
        body: toAssistantAPICommand(request),
      }),
      rawResponseSchema: AssistantResponseSchema.AssistantResponseJSONZ,
      transformResponse: (response: AssistantResponseJSON): AssistantResponse => AssistantResponse.fromJSON(response),
      invalidatesTags: (response) => response === undefined
        ? []
        : [
          { type: "AssistantConversation", id: response.conversationId },
          { type: "AssistantConversation", id: "LIST" },
        ],
    }),
    /**
     * Performs work the assistant prepared and the user has approved.
     *
     * No body: the arguments come from the staged action the server holds. Sending them
     * would turn this into a general-purpose write route that bypasses the real command
     * surface and its validation.
     */
    confirmStagedAction: build.mutation<AssistantResponse, StagedActionRequest>({
      query: ({ stagedActionId }) => ({
        url: `ai-assistant/staged-actions/${stagedActionId}/confirm`,
        method: "POST",
      }),
      rawResponseSchema: AssistantResponseSchema.AssistantResponseJSONZ,
      transformResponse: (response: AssistantResponseJSON): AssistantResponse => AssistantResponse.fromJSON(response),
      // Coarse on purpose while one capability exists. A capability-to-tags map is the
      // shape this wants when there are several.
      invalidatesTags: [{ type: "TeamTemplate", id: "LIST" }],
    }),
    /**
     * The caller's own conversations, most recently used first.
     *
     * No user or legal entity in the request. Both come from the token on the server, so there
     * is no parameter here that could widen what it returns.
     */
    listAssistantConversations: build.query<AssistantConversationSummary[], void>({
      query: () => ({ url: "ai-assistant/conversations", method: "GET" }),
      rawResponseSchema: z.array(AssistantConversationSummarySchema.AssistantConversationSummaryJSONZ),
      transformResponse: (response: AssistantConversationSummaryJSON[]): AssistantConversationSummary[] =>
        response.map((summary) => AssistantConversationSummary.fromJSON(summary)),
      providesTags: [{ type: "AssistantConversation", id: "LIST" }],
    }),
    /**
     * One conversation, with every citation re-checked against what the caller may read now.
     *
     * Not cached across a delete or a new turn - both invalidate it - because the point of the
     * read-time check is that its answer can change between one read and the next.
     */
    readAssistantConversation: build.query<AssistantConversation, AssistantConversationRequest>({
      query: ({ conversationId }) => ({
        url: `ai-assistant/conversations/${conversationId}`,
        method: "GET",
      }),
      rawResponseSchema: AssistantConversationSchema.AssistantConversationJSONZ,
      transformResponse: (response: AssistantConversationJSON): AssistantConversation =>
        AssistantConversation.fromJSON(response),
      providesTags: (_result, _error, { conversationId }) => [
        { type: "AssistantConversation", id: conversationId },
      ],
    }),
    /** Erases one conversation, its turns, its citation excerpts and anything staged in it. */
    deleteAssistantConversation: build.mutation<void, AssistantConversationRequest>({
      query: ({ conversationId }) => ({
        url: `ai-assistant/conversations/${conversationId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "AssistantConversation", id: conversationId },
        { type: "AssistantConversation", id: "LIST" },
      ],
    }),
    /**
     * Erases every conversation the caller has here.
     *
     * Answers with what it left: conversations under legal hold survive an erasure request, and
     * telling the user their history is gone when some of it is not would be the wrong answer.
     */
    deleteAllAssistantConversations: build.mutation<AssistantHistoryErasure, void>({
      query: () => ({ url: "ai-assistant/conversations", method: "DELETE" }),
      invalidatesTags: [{ type: "AssistantConversation", id: "LIST" }],
    }),
    /** Drops a prepared action. Nothing was written, so nothing needs invalidating. */
    cancelStagedAction: build.mutation<AssistantResponse, StagedActionRequest>({
      query: ({ stagedActionId }) => ({
        url: `ai-assistant/staged-actions/${stagedActionId}/cancel`,
        method: "POST",
      }),
      rawResponseSchema: AssistantResponseSchema.AssistantResponseJSONZ,
      transformResponse: (response: AssistantResponseJSON): AssistantResponse => AssistantResponse.fromJSON(response),
    }),
  }),
});

export const {
  useSendAssistantMessageMutation,
  useConfirmStagedActionMutation,
  useCancelStagedActionMutation,
  useListAssistantConversationsQuery,
  useLazyReadAssistantConversationQuery,
  useDeleteAssistantConversationMutation,
  useDeleteAllAssistantConversationsMutation,
} = aiAssistantApi;

function toAssistantAPICommand(request: SendAssistantMessageRequest): AssistantAPICommand {
  return {
    message: request.message,
    conversationId: request.conversationId,
    currentRoute: request.currentRoute,
  };
}
